<fui-heading [text]="'Giveaway Management'" [level]="5"></fui-heading>
<span class="text-muted">Manage giveaway rules that automatically or manually grant products when users register qualifying products.</span>

<div class="m-t-20 m-b-20 d-flex align-items-center gap-3">
  <button class="brand-btn-reverse sm" (click)="openAddModal()">
    <fa-icon icon="plus"></fa-icon>
    Add Giveaway
  </button>
  <div class="form-check m-l-20">
    <input type="checkbox" class="form-check-input" id="includeExpired" [checked]="includeExpired" (change)="toggleIncludeExpired()" />
    <label class="form-check-label" for="includeExpired">Show expired giveaways</label>
  </div>
  @if (total) {
    <span class="text-muted m-l-auto">{{ total }} total</span>
  }
</div>

@if (loading) {
  <myp-inline-loader [loading]="loading" height="200px" spinnerName="giveawayloader"></myp-inline-loader>
}

@if (!loading && !giveaways.length) {
  <div class="text-muted m-t-20">No giveaway rules found.</div>
}

@if (!loading && giveaways.length) {
  <div>
    @for (giveaway of giveaways; track giveaway.id) {
      <myp-list-item
        [title]="(giveaway.entitled_title || 'Product #' + giveaway.entitled_id) + ' → ' + (giveaway.gift_title || 'Product #' + giveaway.gift_id)"
        [subData]="subDataMap[giveaway.id]"
        [includeChevron]="false"
        [showImage]="false"
      >
        <div class="button-content">
          <fa-icon icon="pencil" placement="left" ngbTooltip="Edit Giveaway" (click)="openEditModal(giveaway)" class="action circle-icon-btn m-r-10"></fa-icon>
          <fa-icon
            [icon]="deletingMap[giveaway.id] ? 'spinner' : 'times'"
            [animation]="deletingMap[giveaway.id] ? spinAnimation : undefined"
            placement="left"
            ngbTooltip="Delete Giveaway"
            (click)="deleteGiveaway(giveaway)"
            class="action circle-icon-btn"
          ></fa-icon>
        </div>
      </myp-list-item>
    }
  </div>
  <myp-paginator [offset]="page" [limit]="limit" [resultsLength]="giveaways.length" [totalPages]="totalPages" [totalResults]="total" [paginate]="paginate"></myp-paginator>
}

<ng-template #addEditGiveawayModal let-modal>
  <div class="modal-header">
    <h4 class="modal-title">{{ editing ? "Edit Giveaway" : "Add Giveaway" }}</h4>
    <button ngbAutofocus class="close" aria-label="Close" (click)="modal.dismiss('Cross click')">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
  <div class="modal-body">
    <div class="row">
      <!-- Entitled Product (qualifying product) -->
      <div class="form-group col-md-6">
        <label class="control-label">Entitled Product (qualifying product)</label>
        @if (selectedEntitledProduct) {
          <div class="d-flex align-items-center p-10 secondary-content-bg border-radius10">
            <span class="flex-grow-1">{{ selectedEntitledProduct.title }} (ID: {{ selectedEntitledProduct.id }})</span>
            <fa-icon icon="times" class="action red cursor-pointer" (click)="clearEntitledProduct()"></fa-icon>
          </div>
        } @else {
          <div>
            <input class="form-control" placeholder="Search for a product..." [ngModel]="entitledProductQuery" (ngModelChange)="onEntitledQueryChange($event)" />
            @if (searchingEntitledProducts) {
              <small class="text-muted">
                <fa-icon icon="spinner" animation="spin"></fa-icon>
                Searching...
              </small>
            }
            @if (entitledProductResults?.length) {
              <div class="product-results m-t-5 trans-bg border-radius10 p-10">
                @for (product of entitledProductResults; track product.id) {
                  <myp-list-item [title]="product.title" [subData]="['ID: ' + product.id]" [includeChevron]="false" [showImage]="false">
                    <div class="button-content">
                      <fa-icon icon="plus" ngbTooltip="Select" (click)="selectEntitledProduct(product)" class="action green circle-icon-btn"></fa-icon>
                    </div>
                  </myp-list-item>
                }
              </div>
            }
          </div>
        }
      </div>

      <!-- Gift Product -->
      <div class="form-group col-md-6">
        <label class="control-label">Gift Product{{ !editing ? "(s)" : "" }} (product to give)</label>
        @if (editing) {
          @if (selectedGiftProduct) {
            <div class="d-flex align-items-center p-10 secondary-content-bg border-radius10">
              <span class="flex-grow-1">{{ selectedGiftProduct.title }} (ID: {{ selectedGiftProduct.id }})</span>
              <fa-icon icon="times" class="action red cursor-pointer" (click)="clearGiftProduct()"></fa-icon>
            </div>
          } @else {
            <div>
              <input class="form-control" placeholder="Search for a product..." [ngModel]="giftProductQuery" (ngModelChange)="onGiftQueryChange($event)" />
              @if (searchingGiftProducts) {
                <small class="text-muted">
                  <fa-icon icon="spinner" animation="spin"></fa-icon>
                  Searching...
                </small>
              }
              @if (giftProductResults?.length) {
                <div class="product-results m-t-5 trans-bg border-radius10 p-10">
                  @for (product of giftProductResults; track product.id) {
                    <myp-list-item [title]="product.title" [subData]="['ID: ' + product.id]" [includeChevron]="false" [showImage]="false">
                      <div class="button-content">
                        <fa-icon icon="plus" ngbTooltip="Select" (click)="selectGiftProduct(product)" class="action green circle-icon-btn"></fa-icon>
                      </div>
                    </myp-list-item>
                  }
                </div>
              }
            </div>
          }
        } @else {
          @if (selectedGiftProducts.length) {
            <div class="m-b-10">
              @for (gift of selectedGiftProducts; track gift.id) {
                <div class="d-flex align-items-center p-10 m-b-5 secondary-content-bg border-radius10">
                  <span class="flex-grow-1">{{ gift.title }} (ID: {{ gift.id }})</span>
                  <fa-icon icon="times" class="action red cursor-pointer" (click)="removeGiftProduct(gift)"></fa-icon>
                </div>
              }
            </div>
          }
          <div>
            <input class="form-control" placeholder="Search to add gift products..." [ngModel]="giftProductQuery" (ngModelChange)="onGiftQueryChange($event)" />
            @if (searchingGiftProducts) {
              <small class="text-muted">
                <fa-icon icon="spinner" animation="spin"></fa-icon>
                Searching...
              </small>
            }
            @if (giftProductResults?.length) {
              <div class="product-results m-t-5 trans-bg border-radius10 p-10">
                @for (product of giftProductResults; track product.id) {
                  <myp-list-item [title]="product.title" [subData]="['ID: ' + product.id]" [includeChevron]="false" [showImage]="false">
                    <div class="button-content">
                      <fa-icon icon="plus" ngbTooltip="Add" (click)="selectGiftProduct(product)" class="action green circle-icon-btn"></fa-icon>
                    </div>
                  </myp-list-item>
                }
              </div>
            }
          </div>
        }
      </div>

      <!-- Dates -->
      <div class="form-group col-md-6">
        <label class="control-label">Start Date</label>
        <input type="date" class="form-control" [(ngModel)]="editingGiveaway.startDate" />
      </div>
      <div class="form-group col-md-6">
        <label class="control-label">End Date</label>
        <input type="date" class="form-control" [(ngModel)]="editingGiveaway.endDate" />
      </div>

      <!-- Boolean Flags -->
      <div class="form-group col-md-6">
        <div class="form-check m-b-10">
          <input type="checkbox" class="form-check-input" id="auto_gift" [(ngModel)]="editingGiveaway.auto_gift" />
          <label class="form-check-label" for="auto_gift">Auto Gift</label>
          <small class="d-block text-muted">Automatically grant the gift product upon registration</small>
        </div>
        <div class="form-check m-b-10">
          <input type="checkbox" class="form-check-input" id="allow_customer_claim" [(ngModel)]="editingGiveaway.allow_customer_claim" />
          <label class="form-check-label" for="allow_customer_claim">Allow Customer Claim</label>
          <small class="d-block text-muted">Allow users to manually claim the gift</small>
        </div>
      </div>
      <div class="form-group col-md-6">
        <div class="form-check m-b-10">
          <input type="checkbox" class="form-check-input" id="allowMultiple" [(ngModel)]="editingGiveaway.allowMultiple" />
          <label class="form-check-label" for="allowMultiple">Allow Multiple</label>
          <small class="d-block text-muted">Allow users to receive this gift more than once</small>
        </div>
        <div class="form-check m-b-10">
          <input type="checkbox" class="form-check-input" id="date_of_purchase_check" [(ngModel)]="editingGiveaway.date_of_purchase_check" />
          <label class="form-check-label" for="date_of_purchase_check">Date of Purchase Check</label>
          <small class="d-block text-muted">Only eligible if parent product was acquired within start/end date window</small>
        </div>
      </div>

      <!-- Allowed Countries -->
      <div class="form-group col-md-6">
        <label class="control-label">Allowed Countries</label>
        <input class="form-control" placeholder="e.g. US,CA,GB (leave empty for all)" [(ngModel)]="editingGiveaway.allowed_countries" />
        <small class="text-muted">Comma-separated ISO country codes. Empty = all countries.</small>
      </div>

      <!-- Key Duration -->
      <div class="form-group col-md-6">
        <label class="control-label">Key Duration (months)</label>
        <input type="number" class="form-control" placeholder="Leave empty for permanent" [(ngModel)]="editingGiveaway.key_duration" />
        <small class="text-muted">Number of months before the gifted key expires. Empty = no expiration.</small>
      </div>
    </div>
  </div>
  <div class="modal-footer">
    <button class="btn btn-primary" [disabled]="saving || !selectedEntitledProduct || (editing && !selectedGiftProduct) || (!editing && !selectedGiftProducts.length)" (click)="submit()">
      @if (saving) {
        <fa-icon icon="spinner" animation="spin" class="m-r-5"></fa-icon>
      }
      {{ saving ? "Saving..." : editing ? "Update Giveaway" : "Create Giveaway" }}
    </button>
  </div>
</ng-template>
